import React, { useMemo } from 'react'
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, alpha } from '@mui/material'
import { AppCardTitle, AppEmptyLine } from '@components'
import { dsColorNew, dataColors } from '@palette'

/**
 * Performance Matrix — heatmap-style grid of Category × Channel,
 * colour-coded by YoY % of the active metric.
 *
 * Reads the same payload shape as MekkoChart did (`columns[].segments[]`)
 * but renders cells of uniform size — much more legible when there
 * are 30+ SIPPs.
 *
 *   <PerformanceMatrix columns={...} metric="revenue"
 *                      topN={15}
 *                      onCellClick={(channelKey, sipp) => ...}/>
 */
const fmtEur = (n) =>
	new Intl.NumberFormat('es-ES', {
		style: 'currency', currency: 'EUR',
		minimumFractionDigits: 0, maximumFractionDigits: 0,
	}).format(n || 0)
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n || 0)
const fmtPct = (n) => (n == null ? '—' : `${n > 0 ? '+' : ''}${n.toFixed(1)}%`)

const METRICS = {
	revenue:  { label: 'Revenue',  format: fmtEur },
	bookings: { label: 'Bookings', format: fmtNum },
	days:     { label: 'Days',     format: fmtNum },
	rpd:      { label: 'RPD',      format: fmtEur },
}

// Soft tints, as the price index of Market: green up, grey flat (±3%), red down; the ±30% moves a step stronger
function colorForDelta(yoy) {
	if (yoy == null) return { bg: dsColorNew.filterInputBg, fg: dsColorNew.textSubtle }
	if (yoy >= 30)  return { bg: dataColors.mint300, fg: dataColors.green900 }
	if (yoy >= 10)  return { bg: dataColors.green100, fg: dsColorNew.successText }
	if (yoy >= 3)   return { bg: dsColorNew.successBg, fg: dsColorNew.successText }
	if (yoy > -3)   return { bg: dsColorNew.pillTrackBg, fg: dataColors.gray600 }
	if (yoy > -10)  return { bg: dsColorNew.dangerBg, fg: dataColors.red700 }
	if (yoy > -30)  return { bg: dataColors.red100, fg: dataColors.red700 }
	return { bg: dataColors.red300, fg: dataColors.red900 }
}

function tooltipBody(cell, channelLabel, sipp) {
	if (!cell) {
		return (
			<Box sx={{ p: 0.5 }}>
				<Typography sx={{ fontSize: 13, fontWeight: 600 }}>
					{channelLabel} · {sipp}
				</Typography>
				<Typography sx={{ fontSize: 12, opacity: 0.8 }}>
					No data for this combination.
				</Typography>
			</Box>
		)
	}
	const m = cell.metrics
	const row = (label, key) =>
		`${label}: ${
			key === 'revenue' || key === 'rpd' ? fmtEur(m[key]) : fmtNum(m[key])
		}  ·  PY ${
			key === 'revenue' || key === 'rpd' ? fmtEur(m[`${key}_py`]) : fmtNum(m[`${key}_py`])
		}  ·  ${fmtPct(m[`${key}_yoy`])}`
	return (
		<Box sx={{ p: 0.5 }}>
			<Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
				{channelLabel} · {sipp}
			</Typography>
			<Typography sx={{ fontSize: 12, whiteSpace: 'pre-line' }}>
				{[
					row('Revenue',  'revenue'),
					row('Bookings', 'bookings'),
					row('Days',     'days'),
					row('RPD',      'rpd'),
				].join('\n')}
			</Typography>
		</Box>
	)
}

export default function PerformanceMatrix({
	columns = [],
	metric = 'revenue',
	topN = 15,
	onCellClick,
}) {
	const meta = METRICS[metric] || METRICS.revenue

	// Pivot: index by (channel, sipp) and collect every distinct sipp
	// with its total revenue so we can pick the top-N rows.
	const { rowOrder, otherSipps, cellAt, sippTotals } = useMemo(() => {
		const cellAt = new Map()   // `${channel}|${sipp}` → segment
		const sippTotals = new Map() // sipp → total revenue across all channels
		for (const col of columns) {
			for (const seg of col.segments || []) {
				cellAt.set(`${col.key}|${seg.key}`, seg)
				sippTotals.set(seg.key, (sippTotals.get(seg.key) || 0) + (seg.value || 0))
			}
		}
		const all = Array.from(sippTotals.entries())
			.sort((a, b) => b[1] - a[1])
			.map(([sipp]) => sipp)
		const rowOrder   = all.slice(0, topN)
		const otherSipps = all.slice(topN)
		return { rowOrder, otherSipps, cellAt, sippTotals }
	}, [columns, topN])

	if (!columns.length || !rowOrder.length) {
		return (
			<Paper sx={{ p: 2.5 }}>
				<AppEmptyLine>No data for the current filters.</AppEmptyLine>
			</Paper>
		)
	}

	// Build the synthetic "Other" row from the leftovers per channel.
	const otherRow = otherSipps.length
		? Object.fromEntries(columns.map(col => {
			const segs = (col.segments || []).filter(s => otherSipps.includes(s.key))
			if (!segs.length) return [col.key, null]
			const agg = aggregateSegments(segs)
			return [col.key, { key: 'OTHER', label: 'Other', value: agg.revenue, metrics: agg }]
		}))
		: null

	const cellSx = (yoy, clickable) => {
		const { bg, fg } = colorForDelta(yoy)
		return {
			bgcolor: bg, color: fg,
			borderRadius: '6px',
			minHeight: 42,
			px: 0.75, py: 0.5,
			display: 'flex', flexDirection: 'column',
			alignItems: 'center', justifyContent: 'center',
			fontVariantNumeric: 'tabular-nums',
			cursor: clickable ? 'pointer' : 'default',
			transition: 'transform 80ms ease',
			'&:hover': clickable ? { transform: 'scale(1.04)' } : {},
		}
	}

	const cellBody = (cell, yoy, value) => cell ? (
		<>
			<Box sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.15 }}>{fmtPct(yoy)}</Box>
			<Box sx={{ fontSize: 11, color: dsColorNew.textMuted, lineHeight: 1.15, mt: 0.25 }}>{meta.format(value)}</Box>
		</>
	) : (
		<Box sx={{ fontWeight: 600 }}>—</Box>
	)

	return (
		<Paper sx={{ p: 2.5 }}>
			<Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
				<AppCardTitle icon="table-cells" title={`Performance Matrix — colour by ${meta.label} YoY %`}/>
			</Box>

			<TableContainer sx={matrixSx}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell className="fx">Category</TableCell>
							{columns.map(col => (
								<TableCell key={col.key} align="center">{`${col.label} · ${col.share_pct}%`}</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rowOrder.map(sipp => (
							<TableRow key={sipp}>
								<TableCell className="fx">
									<Box component="span" sx={{ fontWeight: 600, color: dsColorNew.textTitle }}>{sipp}</Box>
									<Box component="span" sx={{ color: dsColorNew.textMuted, ml: 0.75, fontSize: 12 }}>
										{fmtEur(sippTotals.get(sipp))}
									</Box>
								</TableCell>
								{columns.map(col => {
									const cell = cellAt.get(`${col.key}|${sipp}`) || null
									const yoy = cell ? cell.metrics[`${metric}_yoy`] : null
									const value = cell ? cell.metrics[metric] : null
									const clickable = Boolean(onCellClick && cell)
									return (
										<TableCell key={col.key} className="c">
											<Tooltip arrow placement="top" title={tooltipBody(cell, col.label, sipp)}>
												<Box onClick={clickable ? () => onCellClick(col.key, sipp) : undefined}
												     sx={cellSx(yoy, clickable)}>
													{cellBody(cell, yoy, value)}
												</Box>
											</Tooltip>
										</TableCell>
									)
								})}
							</TableRow>
						))}

						{/* "Other" aggregated row */}
						{otherRow && (
							<TableRow className="other">
								<TableCell className="fx">
									<Box component="span" sx={{ fontStyle: 'italic', fontWeight: 600, color: dsColorNew.textMuted }}>
										Other ({otherSipps.length})
									</Box>
								</TableCell>
								{columns.map(col => {
									const cell = otherRow[col.key]
									const yoy = cell ? cell.metrics[`${metric}_yoy`] : null
									const value = cell ? cell.metrics[metric] : null
									return (
										<TableCell key={col.key} className="c">
											<Tooltip arrow placement="top"
											         title={tooltipBody(cell, col.label, `Other (${otherSipps.length})`)}>
												<Box sx={cellSx(yoy, false)}>{cellBody(cell, yoy, value)}</Box>
											</Tooltip>
										</TableCell>
									)
								})}
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted, mt: 1.5, '& strong': { color: dsColorNew.textDark } }}>
				Top {Math.min(topN, rowOrder.length)} categories by revenue.
				Colour = <strong>{meta.label}</strong> YoY %.
				Greys = no movement (±3%); dark green / dark red = strong moves (±30%+).
				Click a cell to drill into partners.
			</Typography>
		</Paper>
	)
}


// Navy header (from the module's table look), the category column fixed and white, the cells as tinted tiles
const matrixSx = {
	'& .MuiTableCell-root': { whiteSpace: 'nowrap' },
	'& .MuiTableBody-root .MuiTableCell-root.c': { p: '3px', minWidth: 96, backgroundColor: `${dsColorNew.white} !important` },
	'& .fx': {
		position: 'sticky', left: 0, zIndex: 2, minWidth: 150,
		boxShadow: `6px 0 8px -6px ${alpha(dsColorNew.textTitle, 0.18)}`,
	},
	'& .MuiTableBody-root .fx': { backgroundColor: `${dsColorNew.white} !important` },
	'& .MuiTableHead-root .fx': { zIndex: 3 },
	'& .other .MuiTableCell-root': { borderTop: `1px dashed ${dsColorNew.grayBorder}` },
}

function aggregateSegments(segs) {
	// Sum metric block across N segments (used for the "Other" row).
	const out = {
		revenue: 0, bookings: 0, days: 0,
		revenue_py: 0, bookings_py: 0, days_py: 0,
	}
	for (const s of segs) {
		const m = s.metrics || {}
		out.revenue     += m.revenue     || 0
		out.bookings    += m.bookings    || 0
		out.days        += m.days        || 0
		out.revenue_py  += m.revenue_py  || 0
		out.bookings_py += m.bookings_py || 0
		out.days_py     += m.days_py     || 0
	}
	out.rpd    = out.days    ? Math.round((out.revenue    / out.days)    * 100) / 100 : 0
	out.rpd_py = out.days_py ? Math.round((out.revenue_py / out.days_py) * 100) / 100 : 0
	for (const k of ['revenue', 'bookings', 'days', 'rpd']) {
		const py = out[`${k}_py`] || 0
		const cur = out[k] || 0
		out[`${k}_yoy`] = py ? Math.round(((cur - py) / py) * 1000) / 10 : null
	}
	return out
}
